import { ArrowRight, InfoIcon } from 'lucide-react'
import { ReactNode, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useGetProject } from '@/api/assessments.hook'
import { useGetCompany } from '@/api/company.hook'
import { useIsControlCommentRequired } from '@/api/control-status-comment-setting.hook'
import { useGetControl, useUpdateControl } from '@/api/control.hook'
import { ControlStatus } from '@/gen/controls/v1/control_status_pb'
import { ControlStatusChangeReason } from '@/gen/inventory/v1/control_service_pb'

import { controlStatusLabel } from '@/const/label'

import { EventProperties, useTrackCallback } from '@/lib/analytics/events'
import { cn } from '@/lib/ui/style-helpers'
import { useUrlParams } from '@/lib/url/url-param.hook'

import { FormControl } from '@/components/lemmala-design-system/form-control/form-control'
import { Loading } from '@/components/lemmala-design-system/loading/loading'
import { Modal } from '@/components/lemmala-design-system/modal/modal'
import {
  RadioGroup,
  RadioGroupItem,
} from '@/components/lemmala-design-system/radio-group/radio-group'
import { Textarea } from '@/components/lemmala-design-system/textarea/textarea'
import { useToast } from '@/components/lemmala-design-system/toast/use-toast'
import { ControlStatusBadge } from '@/components/shared/control-status-badge/control-status-badge'

import { CONTROL_STATUS_CHANGE_MODAL_LABELS } from './labels'

type ControlStatusChangeTriggerProps = {
  openChangeModal: (newStatus: ControlStatus) => void
}

type ControlStatusChangeModalProps = {
  controlId: string
  controlTitle?: string
  status: ControlStatus
  originalAiStatus?: ControlStatus
  assessmentId?: string
  assessmentName?: string
  extraTrackingProps?: EventProperties
  readOnly?: boolean
  children: (props: ControlStatusChangeTriggerProps) => ReactNode
}

const supportedFeedbackStatuses = new Set<ControlStatus>([
  ControlStatus.GAP,
  ControlStatus.VALIDATED,
  ControlStatus.NO_EVIDENCE,
])

const getIncorrectAiEvaluationSubtext = (
  previousStatus: ControlStatus,
  newStatus: ControlStatus,
) => {
  const previousLabel = controlStatusLabel[previousStatus]
  const newLabel = controlStatusLabel[newStatus]

  if (!previousLabel || !newLabel || previousStatus === newStatus) {
    return null
  }

  if (
    previousStatus === ControlStatus.VALIDATED ||
    (previousStatus === ControlStatus.GAP && newStatus === ControlStatus.NO_EVIDENCE)
  ) {
    return CONTROL_STATUS_CHANGE_MODAL_LABELS.aiEvaluationNotSupported
  }

  return CONTROL_STATUS_CHANGE_MODAL_LABELS.aiEvaluationMissedEvidence
}

export const ControlStatusChangeModal = ({
  controlId,
  controlTitle,
  status,
  originalAiStatus,
  assessmentId,
  assessmentName,
  extraTrackingProps,
  readOnly = false,
  children,
}: ControlStatusChangeModalProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedStatus, setSelectedStatus] = useState<ControlStatus | null>(null)
  const [comment, setComment] = useState('')
  const [selectedReason, setSelectedReason] = useState<ControlStatusChangeReason | null>(null)

  const hasRequiredMetadata =
    originalAiStatus != null && assessmentId && assessmentName && controlTitle
  const shouldLoadMetadata = !hasRequiredMetadata
  const { data: controlData, isLoading: isControlLoading } = useGetControl(controlId, {
    enabled: shouldLoadMetadata,
  })
  const isMetadataLoading = shouldLoadMetadata && isControlLoading

  const effectiveOriginalAiStatus = originalAiStatus ?? controlData?.analysisResult?.status
  const effectiveAssessmentId = assessmentId ?? controlData?.control?.ongoingAssessmentId
  const effectiveAssessmentName = assessmentName ?? controlData?.control?.latestAssessmentName

  const isCommentRequired = useIsControlCommentRequired({ enabled: isModalOpen })
  const isMissingRequiredComment = isCommentRequired && comment.trim() === ''

  const { mutateAsync: updateControl } = useUpdateControl(controlId)
  const { urlParams } = useUrlParams()
  const { thirdPartyId } = useParams<{ thirdPartyId?: string }>()
  const { data } = useGetCompany(thirdPartyId || '')
  const { project } = useGetProject(urlParams.projectId ?? '')
  const trackControlValidationUpdate = useTrackCallback('control.validation.update')

  const { toast } = useToast()

  const aiEvaluationSubtext = useMemo(() => {
    if (
      selectedStatus == null ||
      effectiveOriginalAiStatus == null ||
      !supportedFeedbackStatuses.has(effectiveOriginalAiStatus) ||
      !supportedFeedbackStatuses.has(selectedStatus)
    ) {
      return null
    }

    return getIncorrectAiEvaluationSubtext(effectiveOriginalAiStatus, selectedStatus)
  }, [effectiveOriginalAiStatus, selectedStatus])

  const reasonOptions = useMemo(
    () => [
      {
        value: ControlStatusChangeReason.BUSINESS_DECISION,
        title:
          CONTROL_STATUS_CHANGE_MODAL_LABELS.reasonLabel[
            ControlStatusChangeReason.BUSINESS_DECISION
          ],
        description: CONTROL_STATUS_CHANGE_MODAL_LABELS.reasonOptionDescription,
      },
      ...(aiEvaluationSubtext
        ? [
            {
              value: ControlStatusChangeReason.INCORRECT_AI_EVALUATION,
              title:
                CONTROL_STATUS_CHANGE_MODAL_LABELS.reasonLabel[
                  ControlStatusChangeReason.INCORRECT_AI_EVALUATION
                ],
              description: aiEvaluationSubtext,
            },
          ]
        : []),
    ],
    [aiEvaluationSubtext],
  )

  useEffect(() => {
    if (!isModalOpen) {
      return
    }

    if (reasonOptions.length === 0) {
      // oxlint-disable-next-line react/set-state-in-effect
      setSelectedReason(null)
      return
    }

    const hasSelectedReason = reasonOptions.some((option) => option.value === selectedReason)
    if (!hasSelectedReason) {
      // oxlint-disable-next-line react/set-state-in-effect
      setSelectedReason(reasonOptions[0].value)
    }
  }, [isModalOpen, reasonOptions, selectedReason])

  const openChangeModal = (newStatus: ControlStatus) => {
    if (readOnly || newStatus === status) {
      return
    }

    setSelectedStatus(newStatus)
    setComment('')
    setSelectedReason(null)
    setIsModalOpen(true)
  }

  const handleCancel = () => {
    setIsModalOpen(false)
    setSelectedStatus(null)
    setComment('')
    setSelectedReason(null)
  }

  const handleSave = async () => {
    if (!selectedStatus || !selectedReason) {
      return
    }

    trackControlValidationUpdate({
      controlId,
      status: controlStatusLabel[selectedStatus],
      previousStatus: controlStatusLabel[status],
      comment: comment || undefined,
      reason: CONTROL_STATUS_CHANGE_MODAL_LABELS.reasonLabel[selectedReason],
      originalAiStatus: effectiveOriginalAiStatus
        ? controlStatusLabel[effectiveOriginalAiStatus]
        : undefined,
      assessmentId: effectiveAssessmentId,
      assessmentName: effectiveAssessmentName,
      company: data?.company?.profile?.name,
      projectName: project?.name,
      ...extraTrackingProps,
    })

    await updateControl({
      id: controlId,
      status: selectedStatus,
      reason: selectedReason,
      comment: comment || undefined,
    })

    handleCancel()

    toast({
      variant: 'success',
      title: CONTROL_STATUS_CHANGE_MODAL_LABELS.toastTitle,
    })
  }

  return (
    <>
      {children({ openChangeModal })}

      <Modal
        open={isModalOpen}
        size='medium'
        title={CONTROL_STATUS_CHANGE_MODAL_LABELS.modalTitle}
        onCancel={handleCancel}
        primaryButtonText={CONTROL_STATUS_CHANGE_MODAL_LABELS.confirmChange}
        primaryButtonProps={{
          disabled: !selectedReason || isMetadataLoading || isMissingRequiredComment,
        }}
        onOk={handleSave}
      >
        {isMetadataLoading ? (
          <Loading lineCount={2} className='px-0 py-6' />
        ) : (
          <>
            <StatusChangeRender
              beforeStatus={status}
              afterStatus={selectedStatus || status}
              className='my-1'
            />
            <div className='pt-2'>
              {reasonOptions.length > 1 && (
                <>
                  <div className='mb-2 text-main-foreground-secondary'>
                    {CONTROL_STATUS_CHANGE_MODAL_LABELS.selectReason}
                  </div>

                  <RadioGroup
                    value={selectedReason != null ? String(selectedReason) : undefined}
                    onValueChange={(value) =>
                      setSelectedReason(Number(value) as ControlStatusChangeReason)
                    }
                    className='flex flex-col gap-2'
                  >
                    {reasonOptions.map((option) => {
                      const isSelected = selectedReason === option.value

                      return (
                        <label
                          key={option.value}
                          className={cn(
                            'flex cursor-pointer items-start gap-2.5 rounded-[4px] border p-6 transition-colors',
                            isSelected
                              ? 'border-main-border-focus bg-main-background-card shadow-[0_0_0_1px_var(--border-main-border-focus)]'
                              : 'border-main-border-default bg-main-background-card hover:border-main-border-semiBold',
                          )}
                        >
                          <RadioGroupItem
                            value={String(option.value)}
                            id={String(option.value)}
                            className='mt-1'
                          />
                          <div className='min-w-0'>
                            <div className='font-semibold text-main-foreground-primary'>
                              {option.title}
                            </div>
                            <div className='text-main-foreground-secondary'>
                              {option.description}
                            </div>
                          </div>
                        </label>
                      )
                    })}
                  </RadioGroup>
                </>
              )}

              {reasonOptions.length > 1 && (
                <div className='flex h-4 items-center gap-1 py-4 text-main-foreground-accent'>
                  {selectedReason === ControlStatusChangeReason.INCORRECT_AI_EVALUATION && (
                    <>
                      <InfoIcon className='size-4 shrink-0' />
                      <span className='leading-6'>
                        {CONTROL_STATUS_CHANGE_MODAL_LABELS.feedbackNote}
                      </span>
                    </>
                  )}
                </div>
              )}

              <div className='mt-2'>
                <FormControl
                  label={CONTROL_STATUS_CHANGE_MODAL_LABELS.commentLabel}
                  optional={!isCommentRequired}
                >
                  <Textarea
                    placeholder={CONTROL_STATUS_CHANGE_MODAL_LABELS.commentPlaceholder}
                    value={comment}
                    onChange={(event) => setComment(event.target.value)}
                  />
                </FormControl>
              </div>
            </div>
          </>
        )}
      </Modal>
    </>
  )
}

const StatusChangeRender = ({
  beforeStatus,
  afterStatus,
  className,
}: {
  beforeStatus: ControlStatus
  afterStatus: ControlStatus
  className?: string
}) => {
  return (
    <div className={cn('flex items-center gap-3 text-body-small', className)}>
      <div className='flex flex-col gap-1'>
        <span className='text-main-foreground-tertiary'>
          {CONTROL_STATUS_CHANGE_MODAL_LABELS.statusColumnCurrent}
        </span>
        <ControlStatusBadge status={beforeStatus} />
      </div>
      <ArrowRight size={16} className='mx-3' />
      <div className='flex flex-col gap-1'>
        <span className='text-main-foreground-tertiary'>
          {CONTROL_STATUS_CHANGE_MODAL_LABELS.statusColumnNew}
        </span>
        <ControlStatusBadge status={afterStatus} />
      </div>
    </div>
  )
}
